import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ToastStatus = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  status: ToastStatus;
  duration: number;
}

interface ToastOptions {
  title: string;
  description?: string;
  status?: ToastStatus;
  duration?: number;
}

interface ToastContextType {
  toast: (options: ToastOptions) => void;
}

/* ------------------------------------------------------------------ */
/*  Design tokens                                                      */
/* ------------------------------------------------------------------ */

const statusConfig: Record<ToastStatus, {
  bg: string;
  border: string;
  glow: string;
  gradient: string;
  iconGradient: string;
  titleColor: string;
  descColor: string;
  progressGradient: string;
  icon: React.ReactNode;
}> = {
  success: {
    bg: 'rgba(240, 253, 244, 0.72)',
    border: 'rgba(34, 197, 94, 0.25)',
    glow: '0 8px 40px rgba(34,197,94,0.12), 0 0 0 1px rgba(34,197,94,0.08)',
    gradient: 'linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(16,185,129,0.02) 100%)',
    iconGradient: 'linear-gradient(135deg, #22C55E, #10B981)',
    titleColor: '#14532D',
    descColor: '#166534',
    progressGradient: 'linear-gradient(90deg, #22C55E, #10B981, #34D399)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  error: {
    bg: 'rgba(254, 242, 242, 0.72)',
    border: 'rgba(239, 68, 68, 0.25)',
    glow: '0 8px 40px rgba(239,68,68,0.12), 0 0 0 1px rgba(239,68,68,0.08)',
    gradient: 'linear-gradient(135deg, rgba(239,68,68,0.06) 0%, rgba(220,38,38,0.02) 100%)',
    iconGradient: 'linear-gradient(135deg, #EF4444, #DC2626)',
    titleColor: '#7F1D1D',
    descColor: '#991B1B',
    progressGradient: 'linear-gradient(90deg, #EF4444, #DC2626, #F87171)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  warning: {
    bg: 'rgba(255, 251, 235, 0.72)',
    border: 'rgba(245, 158, 11, 0.25)',
    glow: '0 8px 40px rgba(245,158,11,0.12), 0 0 0 1px rgba(245,158,11,0.08)',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(217,119,6,0.02) 100%)',
    iconGradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
    titleColor: '#78350F',
    descColor: '#92400E',
    progressGradient: 'linear-gradient(90deg, #F59E0B, #D97706, #FBBF24)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 5V9" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="8" cy="11.5" r="1" fill="white" />
      </svg>
    ),
  },
  info: {
    bg: 'rgba(239, 246, 255, 0.72)',
    border: 'rgba(59, 130, 246, 0.25)',
    glow: '0 8px 40px rgba(59,130,246,0.12), 0 0 0 1px rgba(59,130,246,0.08)',
    gradient: 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(37,99,235,0.02) 100%)',
    iconGradient: 'linear-gradient(135deg, #3B82F6, #2563EB)',
    titleColor: '#1E3A5F',
    descColor: '#1E40AF',
    progressGradient: 'linear-gradient(90deg, #3B82F6, #2563EB, #60A5FA)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="1" fill="white" />
        <path d="M8 7.5V11.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
};

/* ------------------------------------------------------------------ */
/*  Keyframe Styles                                                    */
/* ------------------------------------------------------------------ */

const keyframeStyles = `
  @keyframes toast-slide-in {
    0% {
      opacity: 0;
      transform: translateX(80px) scale(0.6);
      filter: blur(8px);
    }
    50% {
      opacity: 0.8;
      transform: translateX(-4px) scale(1.02);
      filter: blur(0px);
    }
    70% {
      transform: translateX(2px) scale(0.99);
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
      filter: blur(0px);
    }
  }

  @keyframes toast-slide-out {
    0% {
      opacity: 1;
      transform: translateX(0) scale(1);
      filter: blur(0px);
    }
    100% {
      opacity: 0;
      transform: translateX(60px) scale(0.8);
      filter: blur(4px);
    }
  }

  @keyframes icon-pop {
    0% { transform: scale(0) rotate(-45deg); }
    60% { transform: scale(1.2) rotate(5deg); }
    80% { transform: scale(0.95) rotate(-2deg); }
    100% { transform: scale(1) rotate(0deg); }
  }

  @keyframes icon-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.4); }
    50% { box-shadow: 0 0 0 6px rgba(255,255,255,0); }
  }

  @keyframes progress-shrink {
    from { width: 100%; }
    to { width: 0%; }
  }

  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  @keyframes title-reveal {
    0% { opacity: 0; transform: translateY(4px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  @keyframes desc-reveal {
    0% { opacity: 0; transform: translateY(6px); }
    100% { opacity: 1; transform: translateY(0); }
  }
`;

/* ------------------------------------------------------------------ */
/*  Single Toast                                                       */
/* ------------------------------------------------------------------ */

const SingleToast: React.FC<{
  item: ToastItem;
  onDismiss: (id: string) => void;
}> = ({ item, onDismiss }) => {
  const [phase, setPhase] = useState<'enter' | 'visible' | 'exit'>('enter');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cfg = statusConfig[item.status];

  useEffect(() => {
    const enterTimer = setTimeout(() => setPhase('visible'), 30);
    return () => clearTimeout(enterTimer);
  }, []);

  useEffect(() => {
    if (phase === 'visible') {
      timerRef.current = setTimeout(() => setPhase('exit'), item.duration);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [phase, item.duration]);

  useEffect(() => {
    if (phase === 'exit') {
      const removeTimer = setTimeout(() => onDismiss(item.id), 400);
      return () => clearTimeout(removeTimer);
    }
  }, [phase, item.id, onDismiss]);

  const handleClose = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase('exit');
  };

  const animName = phase === 'exit' ? 'toast-slide-out' : phase === 'visible' ? 'toast-slide-in' : 'none';
  const animDuration = phase === 'exit' ? '0.35s' : '0.55s';

  return (
    <Box
      bg={cfg.bg}
      bgImage={cfg.gradient}
      border="1px solid"
      borderColor={cfg.border}
      borderRadius="18px"
      px={5}
      py={4}
      minW="370px"
      maxW="440px"
      boxShadow={cfg.glow}
      backdropFilter="blur(20px) saturate(180%)"
      opacity={phase === 'enter' ? 0 : undefined}
      animation={animName !== 'none' ? `${animName} ${animDuration} cubic-bezier(0.34, 1.56, 0.64, 1) forwards` : undefined}
      pointerEvents="auto"
      position="relative"
      overflow="hidden"
      cursor="pointer"
      onClick={handleClose}
      role="alert"
      _hover={{
        transform: 'scale(1.015)',
        boxShadow: cfg.glow.replace('0.12', '0.20'),
      }}
      sx={{
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      {/* Subtle shimmer overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        borderRadius="18px"
        opacity={0.4}
        bgSize="200% 100%"
        bgImage="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)"
        animation="shimmer 3s ease-in-out infinite"
        pointerEvents="none"
      />

      <HStack spacing={3.5} align="flex-start" position="relative" zIndex={1}>
        {/* Gradient icon circle */}
        <Box
          w="36px"
          h="36px"
          borderRadius="12px"
          bgImage={cfg.iconGradient}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          boxShadow={`0 4px 14px ${cfg.border}, inset 0 1px 1px rgba(255,255,255,0.3)`}
          animation={phase === 'visible'
            ? 'icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, icon-pulse 2s ease-in-out 0.6s infinite'
            : undefined}
        >
          {cfg.icon}
        </Box>

        {/* Content */}
        <Box flex={1} pt="2px">
          <Text
            fontSize="13.5px"
            fontWeight={700}
            color={cfg.titleColor}
            lineHeight={1.3}
            letterSpacing="-0.02em"
            fontFamily="'DM Sans', sans-serif"
            animation={phase === 'visible' ? 'title-reveal 0.4s ease 0.15s both' : undefined}
          >
            {item.title}
          </Text>
          {item.description && (
            <Text
              fontSize="12px"
              fontWeight={400}
              color={cfg.descColor}
              lineHeight={1.55}
              mt={1}
              opacity={0.85}
              fontFamily="'DM Sans', sans-serif"
              animation={phase === 'visible' ? 'desc-reveal 0.4s ease 0.25s both' : undefined}
            >
              {item.description}
            </Text>
          )}
        </Box>

        {/* Close "×" */}
        <Box
          as="button"
          w="24px"
          h="24px"
          borderRadius="8px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          mt="-2px"
          mr="-4px"
          color={cfg.descColor}
          opacity={0.35}
          bg="transparent"
          border="none"
          cursor="pointer"
          fontSize="16px"
          fontWeight={300}
          transition="all 0.15s ease"
          _hover={{
            opacity: 0.9,
            bg: 'blackAlpha.50',
            transform: 'scale(1.1)',
          }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleClose();
          }}
          aria-label="Close"
        >
          ✕
        </Box>
      </HStack>

      {/* Gradient progress bar */}
      <Box
        mt={3.5}
        h="3px"
        borderRadius="full"
        bg="blackAlpha.50"
        overflow="hidden"
        position="relative"
      >
        <Box
          h="full"
          borderRadius="full"
          bgImage={cfg.progressGradient}
          bgSize="200% 100%"
          animation={phase === 'visible'
            ? `progress-shrink ${item.duration}ms linear forwards, shimmer 1.5s ease-in-out infinite`
            : undefined}
          w={phase === 'visible' ? undefined : '100%'}
          boxShadow={`0 0 8px ${cfg.border}`}
        />
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Context + Provider                                                 */
/* ------------------------------------------------------------------ */

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((options: ToastOptions) => {
    const newToast: ToastItem = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      title: options.title,
      description: options.description,
      status: options.status ?? 'info',
      duration: options.duration ?? 4000,
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}

      {/* Inject keyframes */}
      <style>{keyframeStyles}</style>

      {/* Toast container — fixed top-right */}
      <Box
        position="fixed"
        top="24px"
        right="24px"
        zIndex={9999}
        pointerEvents="none"
      >
        <VStack spacing={4} align="flex-end">
          {toasts.map((item) => (
            <SingleToast key={item.id} item={item} onDismiss={removeToast} />
          ))}
        </VStack>
      </Box>
    </ToastContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export const useAppToast = (): ((options: ToastOptions) => void) => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useAppToast must be used within a ToastProvider');
  return ctx.toast;
};

export default SingleToast;